"use client";

import { MAX_SKUS, MAX_TIERS, MINOR_UNITS } from "@platform/contracts";
import { CircleAlert, Plus } from "lucide-react";
import { Button } from "../../components/button";
import { Icon } from "../../components/icon";
import { Input, Label, Select } from "../../components/input";
import { ErrorText } from "../fields";
import { useText } from "../text";
import { emptySku, emptyTier, type ProductForm, rowKey, type SkuRow } from "./product-form";

const CURRENCIES = Object.keys(MINOR_UNITS);

function Small({
  id,
  label,
  error,
  ...input
}: Parameters<typeof Input>[0] & { id: string; label: string; error?: string | undefined }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : `${id}-error`}
        {...input}
      />
      {error !== undefined && (
        <p id={`${id}-error`} className="flex items-start gap-1.5 text-caption text-tone-problem-fg">
          <Icon icon={CircleAlert} size={16} className="mt-px size-3.5 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * SKUs and their price tiers in the product's one currency (CAT-3), or inquiry-only with MOQs and no prices. Each
 * SKU has a code, a unit, an MOQ, and options that tell it from the others; its first tier starts at the MOQ, and the
 * first tier follows the MOQ while they are equal.
 */
export function PricingFields({
  form,
  onChange,
  issue,
}: {
  form: ProductForm;
  onChange: (changes: Partial<ProductForm>) => void;
  issue: (path: string) => string | undefined;
}) {
  const { t } = useText();
  const setSku = (index: number, changes: Partial<SkuRow>) =>
    onChange({ skus: form.skus.map((sku, s) => (s === index ? { ...sku, ...changes } : sku)) });
  const setMoq = (index: number, sku: SkuRow, moq: string) => {
    const [first, ...rest] = sku.tiers;
    const follows = first !== undefined && (first.minQuantity === "" || first.minQuantity === sku.moq);
    setSku(index, {
      moq,
      tiers: follows && first !== undefined ? [{ ...first, minQuantity: moq }, ...rest] : sku.tiers,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <label className="flex items-center gap-2.5 pb-2 text-body text-ink">
          <input
            type="checkbox"
            className="size-4 accent-brand-600"
            checked={form.inquiryOnly}
            onChange={(event) => onChange({ inquiryOnly: event.target.checked })}
          />
          {t("products.pricing.inquiryOnly")}
        </label>
        {!form.inquiryOnly && (
          <div className="flex w-36 flex-col gap-1.5">
            <Label htmlFor="product-currency">{t("products.pricing.currency")}</Label>
            <Select
              id="product-currency"
              value={form.currency}
              onChange={(event) => onChange({ currency: event.target.value })}
            >
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
      {issue("skus") !== undefined && <ErrorText>{issue("skus")}</ErrorText>}
      {form.skus.map((sku, s) => (
        <fieldset
          key={sku.key}
          className="flex flex-col gap-4 rounded-(--radius-control) border border-border p-4"
        >
          <legend className="sr-only">{t("products.pricing.sku", { number: s + 1 })}</legend>
          <div className="flex items-center justify-between gap-3">
            <span aria-hidden="true" className="text-subheading font-semibold text-ink">
              {t("products.pricing.sku", { number: s + 1 })}
            </span>
            {form.skus.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onChange({ skus: form.skus.filter((_, i) => i !== s) })}
              >
                {t("products.pricing.removeSku")}
              </Button>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Small
              id={`sku-${s}-code`}
              label={t("products.pricing.code")}
              maxLength={64}
              value={sku.code}
              onChange={(event) => setSku(s, { code: event.target.value })}
              error={issue(`skus.${s}.code`)}
            />
            <Small
              id={`sku-${s}-moq`}
              label={t("products.pricing.moq")}
              inputMode="decimal"
              value={sku.moq}
              onChange={(event) => setMoq(s, sku, event.target.value)}
              error={issue(`skus.${s}.moq`)}
            />
            <Small
              id={`sku-${s}-unit`}
              label={t("products.pricing.unit")}
              placeholder={t("products.pricing.unitExample")}
              maxLength={20}
              value={sku.unit}
              onChange={(event) => setSku(s, { unit: event.target.value })}
              error={issue(`skus.${s}.unit`)}
            />
          </div>
          <div className="flex flex-col gap-3 border-t border-border-subtle pt-4">
            <p className="text-small font-semibold text-ink">{t("products.pricing.options")}</p>
            {sku.options.map((option, o) => (
              <div key={option.key} className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <Small
                  id={`sku-${s}-option-${o}-name`}
                  label={t("products.pricing.optionName")}
                  placeholder={t("products.pricing.optionNameExample")}
                  maxLength={40}
                  value={option.name}
                  onChange={(event) =>
                    setSku(s, {
                      options: sku.options.map((each, i) =>
                        i === o ? { ...each, name: event.target.value } : each,
                      ),
                    })
                  }
                />
                <Small
                  id={`sku-${s}-option-${o}-value`}
                  label={t("products.pricing.optionValue")}
                  maxLength={100}
                  value={option.value}
                  onChange={(event) =>
                    setSku(s, {
                      options: sku.options.map((each, i) =>
                        i === o ? { ...each, value: event.target.value } : each,
                      ),
                    })
                  }
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSku(s, { options: sku.options.filter((_, i) => i !== o) })}
                >
                  {t("products.pricing.removeOption")}
                </Button>
              </div>
            ))}
            {issue(`skus.${s}.options`) !== undefined && <ErrorText>{issue(`skus.${s}.options`)}</ErrorText>}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="self-start"
              onClick={() => setSku(s, { options: [...sku.options, { key: rowKey(), name: "", value: "" }] })}
            >
              <Icon icon={Plus} />
              {t("products.pricing.addOption")}
            </Button>
          </div>
          {!form.inquiryOnly && (
            <div className="flex flex-col gap-3 border-t border-border-subtle pt-4">
              <p className="text-small font-semibold text-ink">{t("products.pricing.tiers")}</p>
              {sku.tiers.map((tier, i) => (
                <div key={tier.key} className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                  <Small
                    id={`sku-${s}-tier-${i}-quantity`}
                    label={t("products.pricing.fromQuantity")}
                    inputMode="decimal"
                    value={tier.minQuantity}
                    onChange={(event) =>
                      setSku(s, {
                        tiers: sku.tiers.map((each, j) =>
                          j === i ? { ...each, minQuantity: event.target.value } : each,
                        ),
                      })
                    }
                    error={issue(`skus.${s}.tiers.${i}.minQuantity`)}
                  />
                  <Small
                    id={`sku-${s}-tier-${i}-price`}
                    label={t("products.pricing.unitPrice", { currency: form.currency })}
                    inputMode="decimal"
                    value={tier.price}
                    onChange={(event) =>
                      setSku(s, {
                        tiers: sku.tiers.map((each, j) =>
                          j === i ? { ...each, price: event.target.value } : each,
                        ),
                      })
                    }
                    error={issue(`skus.${s}.tiers.${i}.unitPrice`)}
                  />
                  {i > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setSku(s, { tiers: sku.tiers.filter((_, j) => j !== i) })}
                    >
                      {t("products.pricing.removeTier")}
                    </Button>
                  )}
                </div>
              ))}
              {issue(`skus.${s}.tiers`) !== undefined && <ErrorText>{issue(`skus.${s}.tiers`)}</ErrorText>}
              {sku.tiers.length < MAX_TIERS && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="self-start"
                  onClick={() => setSku(s, { tiers: [...sku.tiers, emptyTier()] })}
                >
                  <Icon icon={Plus} />
                  {t("products.pricing.addTier")}
                </Button>
              )}
            </div>
          )}
        </fieldset>
      ))}
      {form.skus.length < MAX_SKUS && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          onClick={() => onChange({ skus: [...form.skus, emptySku()] })}
        >
          <Icon icon={Plus} />
          {t("products.pricing.addSku")}
        </Button>
      )}
    </div>
  );
}
